<template>
  <div
    class="flex min-w-0 w-full items-center gap-3 p-2 phone:hidden"
    data-test="voice-message-recorder"
  >
    <wui-btn
      variant="negative"
      data-test="voice-record-delete"
      state="text"
      :title="$t('chat.deleteVoiceMessage')"
      prepend-icon="trashM"
      icon
      @click="onDelete"
    />

    <div class="flex min-w-0 flex-1 items-center">
      <div class="flex h-10 w-10 shrink-0 items-center justify-center">
        <div
          v-if="isRecording"
          class="voice-record-dot bg-surf-neg-base-def"
          data-test="voice-record-indicator"
        />
        <wui-btn
          v-if="!isRecording"
          variant="brand"
          state="text"
          :title="$t('chat.playVoiceMessage')"
          :prepend-icon="isPreviewPlaying ? 'pauseF' : 'playF'"
          icon
          @click="onTogglePreview"
        />
      </div>

      <span
        v-if="isRecording"
        class="flex h-10 w-14 shrink-0 items-center text-body-r-16-24 text-fg-base-fg3 tabular-nums"
      >
        {{ durationLabel }}
      </span>
      <div class="flex min-w-0 flex-1 overflow-hidden p-1.5">
        <audio-waveform
          :peaks="session?.peaks ?? []"
          :progress="isRecording ? 0 : previewProgress"
          :bar-count="WAVEFORM_BARS_COUNT"
          @seek="onSeek"
        />
      </div>

      <span
        v-if="showPreviewTime"
        class="flex h-10 w-14 shrink-0 items-center text-body-r-16-24 text-fg-base-fg3 tabular-nums pl-2"
        data-test="voice-record-preview-time"
      >
        {{ previewTimeLabel }}
      </span>
    </div>

    <wui-btn
      v-if="isPaused && canResumeRecording"
      variant="brand"
      data-test="voice-record-resume"
      state="text"
      :title="$t('chat.resumeVoiceRecord')"
      prepend-icon="micM"
      icon
      @click="onResume"
    />

    <wui-btn
      v-if="isRecording"
      variant="brand"
      data-test="voice-record-pause"
      state="text"
      :title="$t('chat.pauseVoiceRecord')"
      prepend-icon="pauseM"
      icon
      @click="onPause"
    />

    <wui-btn
      v-if="isSending"
      variant="neut"
      state="alpha"
      :title="$t('chat.cancelVoiceUpload')"
      prepend-icon="closeRegularMdM"
      icon
      data-test="voice-record-cancel-send"
      @click="onCancelSend"
    >
      <span class="sr-only select-none">{{ $t('chat.cancelVoiceUpload') }}</span>
    </wui-btn>

    <wui-btn
      v-else
      variant="brand"
      state="filled"
      prepend-icon="paperPlaneM"
      icon
      :disabled="isSendDisabled"
      data-test="voice-record-send"
      @click="onSend"
    >
      <span class="sr-only select-none">{{ $t('chat.sendMessage') }}</span>
    </wui-btn>
  </div>

  <div
    class="hidden w-full min-w-0 flex-col phone:flex"
    data-test="voice-message-recorder-mobile"
  >
    <div class="flex min-w-0 items-center gap-2 px-1 pt-1">
      <div class="flex h-10 w-10 shrink-0 items-center justify-center">
        <div
          v-if="isRecording"
          class="voice-record-dot bg-surf-neg-base-def"
          data-test="voice-record-indicator-mobile"
        />
        <wui-btn
          v-else
          variant="brand"
          state="text"
          :title="$t('chat.playVoiceMessage')"
          :prepend-icon="isPreviewPlaying ? 'pauseF' : 'playF'"
          icon
          data-test="voice-record-preview-mobile"
          @click="onTogglePreview"
        />
      </div>

      <span
        v-if="isRecording"
        class="flex h-10 shrink-0 items-center text-body-r-16-24 text-fg-base-fg3 tabular-nums"
      >
        {{ durationLabel }}
      </span>

      <div class="flex min-w-0 flex-1 items-center overflow-hidden py-1.5">
        <audio-waveform
          :peaks="session?.peaks ?? []"
          :progress="isRecording ? 0 : previewProgress"
          :bar-count="WAVEFORM_BARS_COUNT"
          @seek="onSeek"
        />
      </div>

      <span
        v-if="showPreviewTime"
        class="flex h-10 shrink-0 items-center pl-2 text-body-r-16-24 text-fg-base-fg3 tabular-nums"
        data-test="voice-record-preview-time-mobile"
      >
        {{ previewTimeLabel }}
      </span>
    </div>

    <div class="grid grid-cols-3 items-center py-1">
      <div class="flex justify-start">
        <wui-btn
          variant="negative"
          data-test="voice-record-delete-mobile"
          state="text"
          :title="$t('chat.deleteVoiceMessage')"
          prepend-icon="trashM"
          icon
          @click="onDelete"
        />
      </div>

      <div class="flex justify-center">
        <wui-btn
          v-if="isPaused && canResumeRecording"
          variant="brand"
          data-test="voice-record-resume-mobile"
          state="text"
          :title="$t('chat.resumeVoiceRecord')"
          prepend-icon="micM"
          icon
          @click="onResume"
        />
        <wui-btn
          v-else-if="isRecording"
          variant="brand"
          data-test="voice-record-pause-mobile"
          state="text"
          :title="$t('chat.pauseVoiceRecord')"
          prepend-icon="pauseM"
          icon
          @click="onPause"
        />
      </div>

      <div class="flex justify-end">
        <wui-btn
          v-if="isSending"
          variant="neut"
          state="alpha"
          :title="$t('chat.cancelVoiceUpload')"
          prepend-icon="closeRegularMdM"
          icon
          data-test="voice-record-cancel-send-mobile"
          @click="onCancelSend"
        >
          <span class="sr-only select-none">{{ $t('chat.cancelVoiceUpload') }}</span>
        </wui-btn>
        <wui-btn
          v-else
          variant="brand"
          state="filled"
          prepend-icon="paperPlaneM"
          icon
          :disabled="isSendDisabled"
          data-test="voice-record-send-mobile"
          @click="onSend"
        >
          <span class="sr-only select-none">{{ $t('chat.sendMessage') }}</span>
        </wui-btn>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { WuiBtn } from '@wui/common-library'
import { storeToRefs } from 'pinia'
import { computed, toRef } from 'vue'

import {
  AudioWaveform,
  formatVoiceDuration,
  MIN_VOICE_DURATION_MS,
  useVoiceMessageStore,
  VoiceRecorderStatus,
  WAVEFORM_BARS_COUNT,
} from '@/entities/voice-message'

import { Room } from '@/shared/types/store'

import { useVoiceRecorder } from '../model/use-voice-recorder'

const props = defineProps<{
  room: Room
}>()

const room = toRef(props, 'room')
const store = useVoiceMessageStore()
const { sessions } = storeToRefs(store)
const {
  pause,
  resume,
  discard,
  send,
  cancelSend,
  togglePreview,
  seekPreview,
  previewProgress,
  isPreviewPlaying,
  liveRecorderPServed,
} = useVoiceRecorder()

const session = computed(() => sessions.value.get(room.value.pServedUser))
const isRecording = computed(() => session.value?.status === VoiceRecorderStatus.recording)
const isPaused = computed(() => session.value?.status === VoiceRecorderStatus.paused)
const isSending = computed(() => session.value?.status === VoiceRecorderStatus.sending)
const canResumeRecording = computed(() => {
  return isPaused.value && liveRecorderPServed.value === room.value.pServedUser
})
const durationLabel = computed(() => formatVoiceDuration(session.value?.durationMs ?? 0))
const previewTimeLabel = computed(() => {
  const hasStartedPlayback = isPreviewPlaying.value || previewProgress.value > 0
  if (!hasStartedPlayback) return durationLabel.value
  return formatVoiceDuration(previewProgress.value * (session.value?.durationMs ?? 0))
})
const showPreviewTime = computed(() => isPaused.value)
const isSendDisabled = computed(() => {
  return isSending.value || (session.value?.durationMs ?? 0) < MIN_VOICE_DURATION_MS
})

const onDelete = () => discard(room.value.pServedUser)
const onPause = () => pause(room.value.pServedUser)
const onResume = () => resume(room.value.pServedUser)
const onTogglePreview = () => togglePreview(room.value.pServedUser)
const onSeek = (ratio: number) => {
  if (isRecording.value) return
  seekPreview(room.value.pServedUser, ratio)
}
const onSend = () => send(room.value)
const onCancelSend = () => cancelSend(room.value.pServedUser)
</script>

<style scoped>
.voice-record-dot {
  width: 12px;
  height: 12px;
  border-radius: 9999px;
  animation: voice-record-pulse 1.2s ease-in-out infinite;
}

@keyframes voice-record-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.45;
  }
}
</style>
